import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import {
  BarcodeScanner,
  type BarcodeFormat,
} from '@capacitor-mlkit/barcode-scanning'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser'

export type CameraPermissionState = 'granted' | 'denied' | 'prompt'

export interface CameraPermissionResult {
  granted: boolean
  status: CameraPermissionState
}

export class CameraPermissionDeniedError extends Error {
  readonly code = 'PERMISSION_DENIED'
  constructor(message: string = 'Camera access was denied') {
    super(message)
    this.name = 'CameraPermissionDeniedError'
  }
}

export function isCameraPermissionDenied(error: unknown): boolean {
  if (error instanceof CameraPermissionDeniedError) return true
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code: unknown }).code === 'PERMISSION_DENIED'
  ) {
    return true
  }
  if (
    error instanceof Error &&
    (error.name === 'NotAllowedError' ||
      error.name === 'PermissionDeniedError' ||
      error.message.toLowerCase().includes('permission denied'))
  ) {
    return true
  }
  return false
}

export interface ContinuousScanOptions {
  formats?: BarcodeFormat[]
  torch?: boolean
  videoElement?: HTMLVideoElement | null
}

let activeNativeListener: PluginListenerHandle | null = null
let activeZxingControls: IScannerControls | null = null
let isScanning = false
let lastScannedCode = ''
let lastScannedTime = 0

/**
 * Checks whether the native ML Kit BarcodeScanner plugin is available on this platform.
 */
export async function isNativeScannerAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  try {
    const result = await BarcodeScanner.isSupported()
    return Boolean(result?.supported)
  } catch {
    return false
  }
}

/**
 * Requests camera permission from the OS or browser.
 */
export async function requestCameraPermission(): Promise<CameraPermissionResult> {
  if (Capacitor.isNativePlatform()) {
    try {
      const status = await BarcodeScanner.requestPermissions()
      const granted = status?.camera === 'granted'
      return {
        granted,
        status: status?.camera === 'denied' ? 'denied' : granted ? 'granted' : 'prompt',
      }
    } catch {
      return { granted: false, status: 'denied' }
    }
  }

  // Web fallback using getUserMedia check
  if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())
      return { granted: true, status: 'granted' }
    } catch {
      return { granted: false, status: 'denied' }
    }
  }

  return { granted: true, status: 'granted' }
}

/**
 * Starts continuous barcode scanning.
 * - On native: uses ML Kit BarcodeScanner.startScan with transparent WebView class.
 * - On web: uses @zxing/browser with provided HTML videoElement.
 * - Deduplicates repeated codes within 1.5 seconds.
 * - Triggers native haptics on successful read.
 * - Returns an asynchronous stop function.
 */
export async function startContinuousScan(
  onCode: (code: string) => void,
  options?: ContinuousScanOptions,
): Promise<() => Promise<void>> {
  await stopScan()

  lastScannedCode = ''
  lastScannedTime = 0
  isScanning = true

  const handleCode = (rawCode: string) => {
    const code = rawCode.trim()
    if (!code) return
    const now = Date.now()
    if (code === lastScannedCode && now - lastScannedTime < 1500) {
      return
    }
    lastScannedCode = code
    lastScannedTime = now

    if (Capacitor.isNativePlatform()) {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
    }

    onCode(code)
  }

  const isNative = await isNativeScannerAvailable()

  if (isNative) {
    const permission = await requestCameraPermission()
    if (!permission.granted) {
      isScanning = false
      throw new CameraPermissionDeniedError('Camera permission was denied')
    }

    if (typeof document !== 'undefined') {
      document.body.classList.add('scanner-active')
      document.documentElement.classList.add('scanner-active')
    }

    activeNativeListener = await BarcodeScanner.addListener('barcodesScanned', (event) => {
      if (!isScanning) return
      const detected = event.barcodes?.[0]
      const value = detected?.rawValue || detected?.displayValue
      if (value) {
        handleCode(value)
      }
    })

    await BarcodeScanner.startScan({
      formats: options?.formats,
    })

    if (options?.torch) {
      try {
        await BarcodeScanner.enableTorch()
      } catch {
        // Torch optional; silently ignored
      }
    }
  } else {
    const video = options?.videoElement
    if (video) {
      const reader = new BrowserMultiFormatReader()
      try {
        const controls = await reader.decodeFromVideoDevice(undefined, video, (result, _error, frameControls) => {
          activeZxingControls = frameControls
          if (!isScanning) {
            frameControls.stop()
            activeZxingControls = null
            return
          }
          if (result) {
            const text = result.getText()
            if (text) {
              handleCode(text)
            }
          }
        })
        activeZxingControls = controls
        if (!isScanning) {
          controls.stop()
          activeZxingControls = null
        }
      } catch (caught) {
        isScanning = false
        if (isCameraPermissionDenied(caught)) {
          throw new CameraPermissionDeniedError(caught instanceof Error ? caught.message : undefined)
        }
        throw caught
      }
    }
  }

  return async () => {
    await stopScan()
  }
}

/**
 * Stops continuous scanning and restores the normal app background.
 */
export async function stopScan(): Promise<void> {
  isScanning = false

  if (typeof document !== 'undefined') {
    document.body.classList.remove('scanner-active')
    document.documentElement.classList.remove('scanner-active')
  }

  if (activeNativeListener) {
    try {
      await activeNativeListener.remove()
    } catch {
      // ignore
    }
    activeNativeListener = null
  }

  if (Capacitor.isNativePlatform()) {
    try {
      await BarcodeScanner.stopScan()
    } catch {
      // ignore
    }
  }

  if (activeZxingControls) {
    try {
      activeZxingControls.stop()
    } catch {
      // ignore
    }
    activeZxingControls = null
  }
}

/**
 * Toggles the camera torch on native platforms.
 */
export async function toggleTorch(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      await BarcodeScanner.toggleTorch()
    } catch {
      // Torch optional; silently ignored
    }
  }
}
